import { Form } from 'react-bootstrap'
import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  Alert,
  TextField,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  CircularProgress,
  Button,
  Paper,
  Snackbar,
  IconButton,
  Container,
  Box,
} from '@mui/material';
import { createFeedback, getFeedbackContent, getAvailableCallTypes } from '../actions/feedbackActions';
import { CREATE_FEEDBACK_RESET, GET_FEEDBACK_CONTENT_RESET } from '../constants/feedbackConstants';
import BackdropProgress from '../components/BackdropProgress'
import * as React from 'react';
import CloseIcon from '@mui/icons-material/Close';

const ADD_NEW_CALL_TYPE_VALUE = '__add_new_call_type__';

function Feedback() {
    const [callType, setCallType] = useState('')
    const [openAddCallTypeDialog, setOpenAddCallTypeDialog] = useState(false);
    const [newCustomCallType, setNewCustomCallType] = useState('');

    const [feedbackText, setFeedbackText] = useState('');
    const [openSnackbar, setOpenSnackbar] = React.useState(false);
    const [snackbarMessage, setSnackbarMessage] = React.useState('');

    const dispatch = useDispatch()

    const userLogin = useSelector(state => state.userLogin)
    const { userInfo } = userLogin

    const feedbackCreate = useSelector(state => state.feedbackCreateReducer)
    const { loading: loadingCreate, success: successCreate, error: errorCreate } = feedbackCreate

    const feedbackContentState = useSelector(state => state.feedbackContentReducer)
    const { loading: loadingContent, content: fetchedContent, error: errorContent, success: successContent } = feedbackContentState

    const callTypesState = useSelector(state => state.callTypesReducer)
    const { loading: loadingCallTypes, callTypes, error: errorCallTypes, success: successCallTypes } = callTypesState

    const [locallyAddedCallTypes, setLocallyAddedCallTypes] = useState([]);

    const combinedCallTypes = React.useMemo(() => {
        const allTypes = new Set([...(callTypes || []), ...locallyAddedCallTypes]);
        return Array.from(allTypes).sort();
    }, [callTypes, locallyAddedCallTypes]);

    useEffect(() => {
        dispatch(getAvailableCallTypes());
    }, [dispatch])

    useEffect(() => {
        if (successCreate) {
            setOpenSnackbar(true)
            setSnackbarMessage('Feedback submitted successfully')
            dispatch({ type: CREATE_FEEDBACK_RESET })
            if (callType) {
                dispatch(getFeedbackContent(callType));
            }
        }
    }, [dispatch, successCreate, callType])

    useEffect(() => {
        if (successContent && fetchedContent !== undefined) {
            setFeedbackText(fetchedContent);
        }
    }, [dispatch, successContent, fetchedContent])

    const handleCallTypeChange = (event) => {
        const { value } = event.target;
        if (value === ADD_NEW_CALL_TYPE_VALUE) {
            setFeedbackText('');
            dispatch({ type: GET_FEEDBACK_CONTENT_RESET });
            setOpenAddCallTypeDialog(true);
        } else {
            setCallType(value);
            if (value) {
                dispatch(getFeedbackContent(value));
            } else {
                setFeedbackText('');
                dispatch({ type: GET_FEEDBACK_CONTENT_RESET });
            }
        }
    };

    const handleCloseAddCallTypeDialog = () => {
        setOpenAddCallTypeDialog(false);
        setNewCustomCallType('');
    };

    const handleAddNewCustomCallType = () => {
        const trimmedNewType = newCustomCallType.trim();
        if (trimmedNewType && !combinedCallTypes.includes(trimmedNewType)) {
            setLocallyAddedCallTypes(prev => [...prev, trimmedNewType]);
            setCallType(trimmedNewType);
            setFeedbackText('');
            dispatch({ type: GET_FEEDBACK_CONTENT_RESET });
        }
        handleCloseAddCallTypeDialog();
    };

    const handleSubmit = (e) => {
        e.preventDefault()
        if (!callType.trim() || callType === ADD_NEW_CALL_TYPE_VALUE) {
            setOpenSnackbar(true)
            setSnackbarMessage('Please select or add a Type of Call.')
            return
        }
        if (!feedbackText.trim()) {
            setOpenSnackbar(true)
            setSnackbarMessage('Please enter your feedback.')
            return
        }
        dispatch(createFeedback({ userId: userInfo.id, callType, text: feedbackText.trim() }))
    }

    const handleCloseSnackbar = (event, reason) => {
        if (reason === 'clickaway') {
            return;
        }
        setOpenSnackbar(false);
    };

    const snackbarAction = (
        <React.Fragment>
            <IconButton
                size="small"
                aria-label="close"
                color="inherit"
                onClick={handleCloseSnackbar}
            >
                <CloseIcon fontSize="small" />
            </IconButton>
        </React.Fragment>
    );

    const effectiveLoading = loadingCreate || loadingContent || loadingCallTypes;
    const effectiveError = errorCreate || errorContent || errorCallTypes;

    return (
        <Container maxWidth="lg" sx={{ pt: 1, pb: 4 }}>
            <Box sx={{ maxWidth: 900, mx: 'auto' }}>
                <Paper style={{ padding: '20px', marginTop: '20px' }}>
                    {effectiveLoading && <BackdropProgress />}
                    {effectiveError && <Alert severity="error" sx={{ mb: 2 }}>{effectiveError}</Alert>}

                    <Typography variant="h4" component="h1" gutterBottom>
                        Feedback Checks
                    </Typography>

                    <Typography variant="body1" gutterBottom>
                        Configure feedback checks for different types of calls.
                    </Typography>

                    <Form onSubmit={handleSubmit}>
                        <FormControl fullWidth margin="normal" variant="outlined" sx={{ mb: 3 }}>
                            <InputLabel id="call-type-select-label">Type of Call</InputLabel>
                            <Select
                                labelId="call-type-select-label"
                                id="call-type-select"
                                value={callType}
                                label="Type of Call"
                                onChange={handleCallTypeChange}
                                disabled={loadingCallTypes}
                            >
                                {loadingCallTypes && <MenuItem value=""><CircularProgress size={20} /></MenuItem>}
                                {!loadingCallTypes && combinedCallTypes.length === 0 && successCallTypes && (
                                    <MenuItem value="" disabled>
                                        <em>No call types available. Add one?</em>
                                    </MenuItem>
                                )}
                                {combinedCallTypes.map((type) => (
                                    <MenuItem key={type} value={type}>
                                        {type}
                                    </MenuItem>
                                ))}
                                <MenuItem value={ADD_NEW_CALL_TYPE_VALUE}>
                                    <em>Add New Type...</em>
                                </MenuItem>
                            </Select>
                        </FormControl>

                        <TextField
                            fullWidth
                            multiline
                            rows={4}
                            label="Custom checks"
                            value={feedbackText}
                            onChange={(e) => setFeedbackText(e.target.value)}
                            variant='outlined'
                            margin='normal'
                            InputLabelProps={{
                                shrink: !!feedbackText || !!callType,
                            }}
                            sx={{ mb: 3 }}
                        />
                        
                        <Button 
                            type='submit' 
                            variant='contained' 
                            color="primary"
                            disabled={effectiveLoading}
                        >
                            Update
                        </Button>
                    </Form>

                    <Dialog open={openAddCallTypeDialog} onClose={handleCloseAddCallTypeDialog}>
                        <DialogTitle>Add New Type of Call</DialogTitle>
                        <DialogContent>
                            <DialogContentText>
                                Please enter the name for the new type of call.
                            </DialogContentText>
                            <TextField
                                autoFocus
                                margin="dense"
                                id="newCallType"
                                label="New Type of Call"
                                type="text"
                                fullWidth
                                variant="standard"
                                value={newCustomCallType}
                                onChange={(e) => setNewCustomCallType(e.target.value)}
                            />
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={handleCloseAddCallTypeDialog}>Cancel</Button>
                            <Button onClick={handleAddNewCustomCallType}>Add</Button>
                        </DialogActions>
                    </Dialog>

                    <Snackbar
                        open={openSnackbar}
                        autoHideDuration={3000}
                        onClose={handleCloseSnackbar}
                        message={snackbarMessage}
                        action={snackbarAction}
                        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                    />
                </Paper>
            </Box>
        </Container>
    )
}

export default Feedback; 